import {
	CommandBar,
	ConstrainMode,
	DetailsListLayoutMode,
	Dropdown,
	IButtonStyles,
	IColumn,
	ICommandBarItemProps,
	IconButton,
	IDetailsHeaderProps,
	Label,
	Link,
	MessageBar,
	MessageBarType,
	PrimaryButton,
	SearchBox,
	Selection,
	ShimmeredDetailsList,
	Stack,
	TooltipHost
} from "@fluentui/react"
import { SelectionMode } from "@fluentui/react/lib/Utilities"
import { useCallback, useEffect, useRef, useState } from "react"
import {
	addQualifyingEvent,
	commandBarItemStyles,
	getClassNames,
	getCurrentUserEmail,
	getEnrollementRecordsFromHouseholdMembers,
	IS_ADMIN,
	safeFormatDateValue,
	sortDetailsList,
	sortHouseHoldMembers
} from "../lib/helper"
import { makeACMGCRMApiCall, makeCRMApiCall } from "../services/apiService"
import { IAccount } from "../types/IAccount"
import { IEnrollmentRecord } from "../types/IEnrollmentRecord"
import { IHouseholdMemberEnrollmentManager } from "../types/IHouseholdMember"
import { EditGroupDialog } from "./editGroupDialog"
import { EditHouseholdMemberPanel } from "./editHouseholdMemberPanel"
import { FieldType, filterDataByColumnFilters, IColumnSortKeyAndDirection, IFieldFilters, renderCustomListHeader } from "./Shared/DetailsListFilterCallout"
import { StickeyHeaderDetailsList } from "./Shared/StickeyHeaderDetailsList"
import { TerminateDialog } from "./terminateMemberDialog"
import { TermMemberDialog } from "./termMemberDialog"
import { UpdateStartDateDialog } from "./updateStartDateDialog"
import { UploadCensusDialog } from "./uploadCensusDialog"
import { ViewHouseholdMemberDialog } from "./viewHouseholdMemberDialog"
import { MedicalQuestionnaireDispatch } from "./medicalQuestionnaireDispatch"

export const buttonStyles: IButtonStyles = {
	root: {
		marginRight: 10,
		marginTop: 6,
		height: 30
	}
}

interface EnrollmentManagerProps {
	group: IAccount
	closePanel: (refresh: boolean, navigateToCensusUpload: boolean) => void
}

// export function EnrollmentManagerDialog(props: EnrollmentManagerProps): JSX.Element {
// 	return (
// 		<Dialog
// 			onDismiss={() => {
// 				props.closePanel(false, false)
// 			}}
// 			styles={{
// 				main: {
// 					width: "calc(100vw - 180px) !important",
// 					minWidth: "calc(100vw - 180px) !important",
// 					maxWidth: "calc(100vw - 180px) !important",
// 					height: "calc(100vh - 100px)"
// 				}
// 			}}
// 			hidden={false}
// 			dialogContentProps={{
// 				title: (
// 					<Stack horizontalAlign="center">
// 						<h3 style={{ marginTop: 0 }}>Enrollment Manager: {props.group.name}</h3>
// 					</Stack>
// 				)
// 			}}
// 			modalProps={{
// 				isBlocking: true
// 			}}>
// 			<EnrollmentManager group={props.group} closePanel={props.closePanel} />
// 		</Dialog>
// 	)
// }

export function EnrollmentManager({ group, closePanel }: EnrollmentManagerProps) {
	const [loading, setLoading] = useState<boolean>(true)
	const [allEnrollments, setAllEnrollments] = useState<IEnrollmentRecord[]>([])
	const [filteredEnrollments, setFilteredEnrollments] = useState<IEnrollmentRecord[]>([])
	const [selectedItems, setSelectedItems] = useState<IEnrollmentRecord[]>([])
	const [searchText, setSearchText] = useState<string>("")
	const [viewEmployee, setViewEmployee] = useState<IEnrollmentRecord>()
	const [editEmployee, setEditEmployee] = useState<IEnrollmentRecord>()
	const [addNewEmployee, setAddNewEmployee] = useState<boolean>(false)
	const [allowEditPlans, setAllowEditPlans] = useState<boolean>(false)
	const [allowEditDepts, setAllowEditDepts] = useState<boolean>(false)
	const [terminateEmployee, setTerminateEmployee] = useState<IEnrollmentRecord>()
	const [termEmployee, setTermEmployee] = useState<IEnrollmentRecord>()
	const [updateEmployeeStartDate, setUpdateEmployeeStartDate] = useState<IEnrollmentRecord>()
	const [error, setError] = useState<string>()
	const [editGroup, setEditGroup] = useState<IAccount>()
	const [refreshFlag, setRefreshFlag] = useState<boolean>(false)
	const [showUploadCensusDialog, setShowUploadCensusDialog] = useState<IAccount>(undefined)
	const [selectedBulkEditPlan, setSelectedBulkEditPlan] = useState<string>(undefined)
	const [selectedBulkEditDept, setSelectedBulkEditDept] = useState<string>(undefined)
	const [isSavingPlans, setIsSavingPlans] = useState<boolean>(false)
	const [sortColumn, setSortColumn] = useState<IColumnSortKeyAndDirection>({ key: "name", isDescending: false })
	const [columnFilterEnabled, setColumnFilterEnabled] = useState<IColumn>()
	const [currentFilters, setCurrentFilters] = useState<IFieldFilters>({})

	const selectionRef = useRef(
		new Selection<IEnrollmentRecord>({
			onSelectionChanged: () => {
				setSelectedItems(selectionRef.current.getSelection() as IEnrollmentRecord[])
			}
		})
	)

	const getHouseholdMembers = useCallback(async (): Promise<IHouseholdMemberEnrollmentManager[]> => {
		//const escapedFetchXML = encodeURIComponent(fetchXml)
		const apiUrl = `getHouseholdMembers?accountId=${group.accountid}`

		try {
			const res = await makeACMGCRMApiCall(apiUrl)
			const results = res as IHouseholdMemberEnrollmentManager[]
			const filteredResults: IHouseholdMemberEnrollmentManager[] = []

			results.forEach(r => {
				const foundIndex = filteredResults.findIndex(f => f.cw_householdmembersid === r.cw_householdmembersid)
				if (foundIndex === -1) {
					filteredResults.push(r)
				}
			})

			return sortHouseHoldMembers(filteredResults)
		} catch (error) {
			console.error("Error fetching household members:", error)
			throw error
		}
	}, [group.accountid])

	useEffect(() => {
		setError(undefined)
		const fetchData = async () => {
			try {
				if (group) {
					const householdMembers = await getHouseholdMembers()
					const innerEnrollments = getEnrollementRecordsFromHouseholdMembers(householdMembers)
					setAllEnrollments(innerEnrollments)
					setFilteredEnrollments(innerEnrollments.filter(e => e.relationship === "Employee"))
				}
			} catch (err) {
				console.log(err)
				setError(`Error getting enrollments`)
			} finally {
				setLoading(false)
			}
		}
		fetchData()
	}, [group, getHouseholdMembers, refreshFlag])

	const _onClearFilter = (key: string) => {
		const currentFieldFilters = currentFilters
		delete currentFieldFilters[key] //currentFieldFilters![key]
		setCurrentFilters({ ...currentFieldFilters })
		setColumnFilterEnabled(undefined)
	}

	const sortEnrollments = (a: IEnrollmentRecord, b: IEnrollmentRecord): number => {
		const key = sortColumn.key === "employee_id" ? "lastName" : sortColumn.key
		const column = sortColumn.key === "employee_id" ? undefined : getColumns().find(c => c.key === sortColumn.key)
		return sortDetailsList(
			a,
			b,
			key,
			column?.data?.type || FieldType.Text,
			sortColumn.isDescending ? "desc" : "asc",
			column?.data?.type === FieldType.Custom ? column?.data?.filterValue : undefined
		)
	}

	useEffect(() => {
		let filteredEnrollments = allEnrollments || []
		if (Object.keys(currentFilters).length > 0) {
			filteredEnrollments = filteredEnrollments.filter(company => filterDataByColumnFilters(company, [], currentFilters, getColumns, _onClearFilter))
		}

		if (searchText && searchText !== "") {
			filteredEnrollments = filteredEnrollments.filter(e => {
				let found = false
				Object.keys(e).filter(k => {
					const value = e[k]
					if (typeof value === "string" && value.toLowerCase().indexOf(searchText.toLowerCase()) > -1) {
						found = true
						return
					}
				})
				return found
			})
			setFilteredEnrollments([...filteredEnrollments].sort(sortEnrollments).filter(e => e.relationship === "Employee"))
		} else {
			setFilteredEnrollments([...filteredEnrollments].sort(sortEnrollments).filter(e => e.relationship === "Employee"))
		}
	}, [searchText, allEnrollments, sortColumn, currentFilters])

	const getEmployeeName = (employee: IEnrollmentRecord) => {
		return `${employee.firstName} ${employee.lastName}`
	}

	const getColumns = () => {
		const retVal: IColumn[] = [
			{
				key: "employee_id",
				minWidth: 120,
				name: "Employee",
				headerClassName: "FilterableHeader-employee_id",
				data: {
					filterable: true,
					sortable: true,
					type: FieldType.Custom,
					filterValue: getEmployeeName
				},
				onRender: (item: IEnrollmentRecord) => {
					const hhms = (allEnrollments || []).filter(e => e.employee_id === item.employee_id)
					return (
						<TooltipHost content={hhms.length > 1 ? `View employee and ${hhms.length - 1} household members` : "View employee"}>
							<Link
								onClick={() => {
									setViewEmployee(item)
								}}>
								{getEmployeeName(item)}
							</Link>
						</TooltipHost>
					)
				}
			},
			{
				key: "Edit",
				minWidth: 30,
				name: "",
				isIconOnly: true,
				onRender: (item: IEnrollmentRecord) => {
					return (
						<IconButton
							id="ContextualMenuButton1"
							className={getClassNames().child}
							text=""
							width="30"
							split={false}
							iconProps={{ iconName: "MoreVertical" }}
							menuIconProps={{ iconName: "" }}
							menuProps={{
								shouldFocusOnMount: true,
								items: [
									{
										key: "EditEmployee",
										text: "Edit Employee",
										iconProps: {
											iconName: "Edit"
										},
										onClick: () => setEditEmployee(item)
									},
									{
										key: "Update",
										text: "Update Effective Date",
										iconProps: {
											iconName: "CalendarSettings"
										},
										onClick: () => setUpdateEmployeeStartDate(item)
									},
									{
										key: "Term",
										text: "Term Employee",
										iconProps: {
											iconName: "RemoveEvent"
										},
										onClick: () => setTermEmployee(item)
									}
									// {
									// 	key: "Terminate",
									// 	text: "Remove Employee",
									// 	iconProps: {
									// 		iconName: "Trash"
									// 	},
									// 	onClick: () => setTerminateEmployee(item)
									// }
								]
							}}
						/>
					)
				}
			},
			// {
			// 	key: "Milestone",
			// 	minWidth: 70,
			// 	name: "Milestone",
			// 	onRender: (item: IEnrollmentRecord) => {
			// 		return <span>NotReg</span>
			// 	}
			// },
			{
				key: "birthday",
				minWidth: 70,
				name: "Birthday",
				headerClassName: "FilterableHeader-birthday",
				data: {
					sortable: true,
					type: FieldType.Date
				},
				onRender: (item: IEnrollmentRecord) => {
					return safeFormatDateValue(item.birthday, "MM/dd/yyyy", "N/A")
				}
			},
			{
				key: "email",
				minWidth: 120,
				name: "Email",
				onRender: (item: IEnrollmentRecord) => {
					return item.emailaddress
				},
				isResizable: true
			},
			{
				key: "startCoverage",
				headerClassName: "FilterableHeader-startCoverage",
				minWidth: 70,
				name: "Start Coverage",
				data: {
					filterable: true,
					sortable: true,
					type: FieldType.Text
				},
				onRender: (item: IEnrollmentRecord) => {
					return item.startCoverage
				}
			},
			{
				key: "SSN",
				minWidth: 70,
				name: "SSN",
				onRender: (item: IEnrollmentRecord) => {
					return item.SSN?.length > 5 ? item.SSN : ""
				}
			},
			{
				key: "dept",
				headerClassName: "FilterableHeader-dept",
				minWidth: 70,
				name: "Dept",
				data: {
					filterable: true,
					sortable: true
				},
				onRender: (item: IEnrollmentRecord) => {
					return item.dept
				}
			},
			// {
			// 	key: "cw_zipcode",
			// 	headerClassName: "FilterableHeader-cw_zipcode",
			// 	minWidth: 70,
			// 	name: "Zip",
			// 	data: {
			// 		filterable: true,
			// 		sortable: true,
			// 		type: FieldType.Custom,
			// 		filterValue: (item: IEnrollmentRecord) => item.cw_zipcode || item.cw_hhzip
			// 	},
			// 	onRender: (item: IEnrollmentRecord) => {
			// 		return item.cw_zipcode || item.cw_hhzip
			// 	}
			// },
			{
				key: "insEffDate",
				headerClassName: "FilterableHeader-insEffDate",
				minWidth: 120,
				name: "Ins Eff Date",
				data: {
					filterable: true,
					sortable: true,
					type: FieldType.Date
				},
				onRender: (item: IEnrollmentRecord) => {
					return item.insEffDate ? safeFormatDateValue(item.insEffDate, "MM/dd/yyyy") : "N/A"
				}
			},
			{
				key: "insTermDate",
				headerClassName: "FilterableHeader-insTermDate",
				minWidth: 120,
				name: "Ins Term Date",
				data: {
					filterable: true,
					sortable: true,
					type: FieldType.Date
				},
				onRender: (item: IEnrollmentRecord) => {
					return item.insTermDate ? safeFormatDateValue(item.insTermDate, "MM/dd/yyyy") : ""
				}
			},
			{
				key: "coverageTier",
				headerClassName: "FilterableHeader-coverageTier",
				minWidth: 120,
				name: "Coverage Tier",
				data: {
					filterable: true,
					sortable: true
				},
				onRender: (item: IEnrollmentRecord) => {
					return item.coverageTier
				}
			},
			{
				key: "planSelection",
				headerClassName: "FilterableHeader-planSelection",
				minWidth: 120,
				data: {
					filterable: true,
					sortable: true
				},
				name: "Plan Selection",
				onRender: (item: IEnrollmentRecord) => {
					return item.planSelection
				}
			}
		].filter(Boolean) as IColumn[]

		return retVal
	}

	const bulkSavePlans = async () => {
		setIsSavingPlans(true)
		const promises = selectedItems.map(e => {
			return makeCRMApiCall(`cw_employees(${e.employee_id})`, "PATCH", JSON.stringify({ "cw_Plan@odata.bind": `/cw_universalplanses(${selectedBulkEditPlan})` }))
		})
		const contactEmail = await getCurrentUserEmail()
		try {
			await Promise.all(promises)
			await Promise.all(
				selectedItems.map(e => {
					return addQualifyingEvent(
						contactEmail,
						`Updated Plan ${e.planSelection ? `from ${e.planSelection}` : ""} to ${group.plans.find(plan => plan.cw_universalplansid === selectedBulkEditPlan).cw_name}`,
						e.employee_id
					)
				})
			)
			setRefreshFlag(!refreshFlag)
			setSelectedBulkEditPlan(undefined)
			setSelectedItems([])
			selectionRef.current.setAllSelected(false)
			setIsSavingPlans(false)
		} catch (err) {
			console.error(err)
			setError("Error saving plans")
			setIsSavingPlans(false)
		}
	}
	const bulkSaveDepts = async () => {
		setIsSavingPlans(true)
		const promises = selectedItems.map(e => {
			return makeCRMApiCall(`cw_employees(${e.employee_id})`, "PATCH", JSON.stringify({ "cw_Department@odata.bind": `/cw_departmentses(${selectedBulkEditDept})` }))
		})
		try {
			await Promise.all(promises)
			setRefreshFlag(!refreshFlag)
			setSelectedBulkEditDept(undefined)
			setSelectedItems([])
			selectionRef.current.setAllSelected(false)
			setIsSavingPlans(false)
		} catch (err) {
			console.error(err)
			setError("Error saving departments")
			setIsSavingPlans(false)
		}
	}
	const canUploadCensus = IS_ADMIN || !group.cw_lockunderwriting || !group.cw_markassold || !group.cw_salelost
	const canEditPlans = allEnrollments.length > 0 && canUploadCensus && group.plans?.length > 0 && !allowEditDepts
	const canEditDepts = allEnrollments.length > 0 && IS_ADMIN && group.departments?.length > 0 && !allowEditPlans

	const getCommandBarItems = (): ICommandBarItemProps[] => {
		const items: ICommandBarItemProps[] = [
			canUploadCensus && {
				key: "uploadCensus",
				text: "Upload New Census",
				commandBarButtonAs: () => {
					return (
						<PrimaryButton
							styles={buttonStyles}
							text="Upload New Census"
							onClick={() => {
								closePanel(true, true)
								setShowUploadCensusDialog(group)
							}}
						/>
					)
				}
			},
			!group.cw_salelost && {
				key: "addNewEmployee",
				text: "Add New Employee",
				commandBarButtonAs: () => {
					return <PrimaryButton styles={buttonStyles} text="Add New Employee" onClick={() => setAddNewEmployee(true)} />
				}
			},
			canEditPlans && {
				key: "allowEditPlans",
				text: !allowEditPlans ? "Edit Plans" : "Stop Editing Plans",
				commandBarButtonAs: () => {
					return (
						<PrimaryButton
							styles={{ ...buttonStyles, root: allowEditPlans ? { ...(buttonStyles.root as object), backgroundColor: "#a74949" } : { ...(buttonStyles.root as object) } }}
							text={!allowEditPlans ? "Edit Plans" : "Stop Editing Plans"}
							onClick={() => {
								setAllowEditPlans(!allowEditPlans)
								setSelectedItems([])
								setSelectedBulkEditPlan(undefined)
								selectionRef.current.setAllSelected(false)
							}}
						/>
					)
				}
			},
			allowEditPlans && {
				key: "bulkPlanEdit",
				commandBarButtonAs: () => {
					return selectedItems?.length > 0 ? (
						<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
							<Label>Select Plan for {selectedItems.length} employees</Label>
							<Dropdown
								styles={{
									root: {
										width: 120
									}
								}}
								selectedKey={selectedBulkEditPlan}
								onChange={(_e, o) => setSelectedBulkEditPlan(o.key as string)}
								options={group.plans?.map(p => ({ key: p.cw_universalplansid, text: p.cw_name }))}
							/>
							{selectedBulkEditPlan && (
								<PrimaryButton
									styles={buttonStyles}
									text="Save"
									disabled={isSavingPlans}
									onClick={() => {
										bulkSavePlans()
									}}
								/>
							)}
						</Stack>
					) : (
						<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
							Select employees from the list
						</Stack>
					)
				}
			},
			canEditDepts && {
				key: "allowEditDepts",
				text: !allowEditDepts ? "Edit Departments" : "Stop Editing Departments",
				commandBarButtonAs: () => {
					return (
						<PrimaryButton
							styles={{ ...buttonStyles, root: allowEditDepts ? { ...(buttonStyles.root as object), backgroundColor: "#a74949" } : { ...(buttonStyles.root as object) } }}
							text={!allowEditDepts ? "Edit Departments" : "Stop Editing Departments"}
							onClick={() => {
								setAllowEditDepts(!allowEditDepts)
								setSelectedItems([])
								setSelectedBulkEditDept(undefined)
								selectionRef.current.setAllSelected(false)
							}}
						/>
					)
				}
			},
			allowEditDepts && {
				key: "bulkDeptEdit",
				commandBarButtonAs: () => {
					return selectedItems?.length > 0 ? (
						<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
							<Label>Select Department for {selectedItems.length} employees</Label>
							<Dropdown
								styles={{
									root: {
										width: 120
									}
								}}
								selectedKey={selectedBulkEditDept}
								onChange={(_e, o) => setSelectedBulkEditDept(o.key as string)}
								options={group.departments?.map(p => ({ key: p.cw_departmentsid, text: p.cw_name }))}
							/>
							{selectedBulkEditDept && (
								<PrimaryButton
									styles={buttonStyles}
									text="Save"
									disabled={isSavingPlans}
									onClick={() => {
										bulkSaveDepts()
									}}
								/>
							)}
						</Stack>
					) : (
						<Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
							Select employees from the list
						</Stack>
					)
				}
			}
		].filter(Boolean) as ICommandBarItemProps[]
		return items
	}

	const getCommandBarFarItems = (): ICommandBarItemProps[] => {
		const items: ICommandBarItemProps[] = [
			{
				key: "search",
				commandBarButtonAs: () => {
					return (
						<div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
							<SearchBox
								disableAnimation
								placeholder="Search employees...."
								underlined
								value={searchText}
								onChange={(_, value) => {
									setSearchText(value)
								}}
								styles={{ root: { width: 300 } }}
							/>
						</div>
					)
				},
				buttonStyles: commandBarItemStyles
			}
		]
		return items
	}

	return (
		<>
			<Stack tokens={{ childrenGap: 10 }} style={{ padding: 15 }}>
				{IS_ADMIN && <MedicalQuestionnaireDispatch key={group.accountid} accountId={group.accountid} censusVersion={refreshFlag} />}
				{error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
				{loading && <ShimmeredDetailsList items={[]} columns={getColumns()} enableShimmer />}

				{!loading && !(filteredEnrollments?.length === 0 && searchText === undefined) && (
					<Stack tokens={{ childrenGap: 10 }}>
						{!group.cw_salelost && (
							<CommandBar
								styles={{
									root: {
										backgroundColor: "auto"
									}
								}}
								items={getCommandBarItems()}
								farItems={getCommandBarFarItems()}
							/>
						)}
						<StickeyHeaderDetailsList
							layoutMode={DetailsListLayoutMode.fixedColumns}
							selectionMode={allowEditPlans || allowEditDepts ? SelectionMode.multiple : SelectionMode.none}
							selection={selectionRef.current}
							items={filteredEnrollments || []}
							compact
							columns={getColumns()}
							constrainMode={ConstrainMode.unconstrained}
							onRenderDetailsHeader={(props?: IDetailsHeaderProps, defaultRender?: (props?: IDetailsHeaderProps) => JSX.Element | null) =>
								renderCustomListHeader(columnFilterEnabled, setColumnFilterEnabled, allEnrollments, currentFilters, setCurrentFilters, sortColumn, setSortColumn, props, defaultRender)
							}
						/>
					</Stack>
				)}
				{filteredEnrollments?.length === 0 && searchText === undefined && (
					<span>
						There are no enrollments. Go to the{" "}
						<Link
							onClick={() => {
								closePanel(true, true)
							}}>
							Census Upload
						</Link>{" "}
						to upload employees and household members
					</span>
				)}
			</Stack>

			{editEmployee && (
				<EditHouseholdMemberPanel
					closePanel={(refreshItems: boolean) => {
						if (refreshItems) {
							setRefreshFlag(!refreshFlag)
						}
						setEditEmployee(undefined)
					}}
					householdMember={editEmployee}
					addHouseholdMember={!editEmployee}
					isEmployee
					employeeId={editEmployee.employee_id}
					groupPlans={group.plans}
					group={group}
				/>
			)}
			{addNewEmployee && (
				<EditHouseholdMemberPanel
					closePanel={(refreshItems: boolean) => {
						if (refreshItems) {
							setRefreshFlag(!refreshFlag)
						}
						setAddNewEmployee(undefined)
					}}
					householdMember={undefined}
					addHouseholdMember
					isEmployee
					employeeId={undefined}
					groupPlans={group.plans}
					group={group}
				/>
			)}
			{terminateEmployee && (
				<TerminateDialog
					closePanel={(refreshItems: boolean) => {
						// if (refreshItems) {

						// }
						setTerminateEmployee(undefined)
					}}
					householdMember={terminateEmployee}
				/>
			)}
			{termEmployee && (
				<TermMemberDialog
					closePanel={(refreshItems: boolean) => {
						if (refreshItems) {
							setRefreshFlag(!refreshFlag)
						}
						setTermEmployee(undefined)
					}}
					employeeHouseholdMember={termEmployee}
					householdMembers={allEnrollments.filter(e => e.employee_id === termEmployee.employee_id)}
				/>
			)}
			{updateEmployeeStartDate && (
				<UpdateStartDateDialog
					closePanel={(refreshItems: boolean) => {
						if (refreshItems) {
							setRefreshFlag(!refreshFlag)
						}
						setUpdateEmployeeStartDate(undefined)
					}}
					employeeHouseholdMember={updateEmployeeStartDate}
					householdMembers={allEnrollments.filter(e => e.employee_id === updateEmployeeStartDate.employee_id)}
				/>
			)}
			{viewEmployee && (
				<ViewHouseholdMemberDialog
					closePanel={(refreshItems: boolean) => {
						if (refreshItems) {
							setRefreshFlag(!refreshFlag)
						}
						setViewEmployee(undefined)
					}}
					employeeHouseholdMember={viewEmployee}
					employeeHouseholdMembers={allEnrollments.filter(e => e.employee_id === viewEmployee.employee_id)}
					isEmployee
					employeeId={viewEmployee.employee_id}
					groupPlans={group.plans}
					group={group}
				/>
			)}
			{editGroup && (
				<EditGroupDialog
					closePanel={(refreshItems: boolean) => {
						// if (refreshItems) {

						// }
						setEditGroup(undefined)
					}}
					initialGroup={editGroup}
				/>
			)}
			{showUploadCensusDialog && (
				<UploadCensusDialog
					closePanel={refreshItems => {
						setShowUploadCensusDialog(undefined)
						if (refreshItems) setRefreshFlag(!refreshFlag)
					}}
					group={showUploadCensusDialog}
				/>
			)}
		</>
	)
}
